import { FC, useState, useEffect, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { TableCol, actionArr, FilterArr } from "./Data";
import { TableLayout } from "Layouts";
import { ControlledTable, Filter2 } from "Components";
import { RouteUrls } from "Constant/RouteUrls";
import { useDispatch, useSelector } from "react-redux";
import Formate from "./Formate";
import PatientApi from "Service/Patient.api";
import { addSelectedPrgm } from "Redux/Patient/Action";
import { Filter2DataProps } from "DataTypes/Common";
import { selectedPrgmInitialState } from "Redux/Patient/reducer";
import { ScreenInitialStateType, UserInitialStateType } from "DataTypes/Redux";
import { CompleteCareType } from "DataTypes/Services/Patient.type";
import { CreateFilter2Payload } from "Utils/common";
import CommonFormatter from "Utils/Common.Formatter";
import DateTime from "Utils/DateTime";
import UI from "Constant/UI";
import { setPageLimit, setPageSort } from "Redux/Screen/Action";

const CompleteCare: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { myInfo }: UserInitialStateType = useSelector(
    (state: any) => state?.user
  );
  const { listPagination, listSort }: ScreenInitialStateType = useSelector(
    (state: any) => state?.screen
  );
  const reduxLimit = (listPagination || []).find(
    el => el.id == "completed-care"
  )?.limit || 10;
  const reduxSortAsc = (listSort || []).find(
    el => el.id == "completed-care"
  )?.sortAsc;
  const currentMonth = DateTime.CurrentMonthName();
  const currentYear = DateTime.CurrentYear();
  const [pageData, setPageData] = useState({
    ...UI.pageData,
    limit: reduxLimit
  });
  const [rows, setRows] = useState<CompleteCareType[]>([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(currentMonth);
  const [year, setYear] = useState(currentYear);
  const [searchText, setSearchText] = useState("");
  const [filterHeight, setFilterHeight] = useState<number>(0);
  const [filterData, setFilterData] = useState<Filter2DataProps[]>(
    JSON.parse(JSON.stringify(FilterArr()))
  );
  const tableHeight = `calc(99% - ${filterHeight}px)`;
  const [sortType, setSortType] = useState(
    reduxSortAsc ?? true
  ); // true = "asc" & false = "desc"

  useEffect(() => {
    handleSetData(1, pageData.limit);
  }, [filterData, month, year, sortType]);

  const handleSetData: (
    currentPage: number,
    currentLimit: number
  ) => void = async (currentPage, currentLimit) => {
    setLoading(true);
    const newFilterPayload = CreateFilter2Payload(filterData);
    const payload = {
      ...(newFilterPayload || {}),
      month: DateTime.MonthNameToNumber(month),
      year: DateTime.YearToNumber(year),
    };
    const res = await PatientApi.CompletedContinuous({
      offset: (currentPage - 1) * currentLimit,
      limit: currentLimit,
      data: payload,
      sortDirection: sortType ? "ASC" : "DESC"
    });
    let newPageData = { ...UI.pageData }
    let newRows: CompleteCareType[] = []
    if (res?.success) {
      newRows = Formate.CompleteCareList(res?.data);
      newPageData = res.criterion;
    }
    setRows(newRows);
    setPageData(newPageData)
    dispatch(setPageLimit({ id: "completed-care", limit: newPageData.limit }));
    setLoading(false);
  };

  const handlePatientDetails: (data: CompleteCareType) => void = (data) => {
    navigate(
      `../../${RouteUrls.patient}/${data.patientId}/${RouteUrls.details}`
    );
  };

  const handleChart: (data: CompleteCareType) => void = (data) => {
    navigate(
      `../../${RouteUrls.patient}/${data.patientId}/` +
      `${RouteUrls.clinicalSummary}`
    );
  };

  const handleTask: (data: CompleteCareType) => void = (data) => {
    navigate(
      `../../${RouteUrls.patient}/${data.patientId}/` +
      `${RouteUrls.task}/${RouteUrls.add}`
    );
  };

  const handleViewCare: (data: CompleteCareType) => void = (data) => {
    dispatch(
      addSelectedPrgm({
        ...selectedPrgmInitialState,
        readOnlyArr: [
          "uptoDate",
          "problem",
          "addDevice",
          "editDevice",
          "question",
          "care-team",
          "add-provider",
          "edit-provider",
          "addNotes",
          "frequency",
          "interactive",
          "medically",
        ],
      })
    );
    navigate(
      `/${RouteUrls.hs}/${RouteUrls.patient}/${data.patientId
      }/${CommonFormatter.HandleScreenRoute({
        careState: data.careplanState,
        careId: data.careplanId,
        prgm: data.program,
      })}`
    );
  };

  const handleSortClick: () => void = () => {
    const newSortAsc = !sortType;
    setSortType(newSortAsc);
    dispatch(setPageSort({ id: "completed-care", sortAsc: newSortAsc }));
  };

  const TblBodyJSX = (
    <ControlledTable.TblBody
      tableColumn={() => TableCol(myInfo, handleSortClick)}
      tableRow={rows}
      tableAction={(data: CompleteCareType) =>
        actionArr(
          () => handleChart(data),
          () => handlePatientDetails(data),
          () => handleTask(data),
          () => handleViewCare(data),
          data.program
        )
      }
      searchText={searchText}
    />
  );

  return (
    <Fragment>
      <Filter2
        filterDataArr={filterData}
        handleFilterClick={(data) => setFilterData(data)}
        changeSearchText={(val) => setSearchText(val)}
        setFilterHeight={(val) => setFilterHeight(val)}
        monthChange={(val) => setMonth(val)}
        monthFilter={true}
        monthVal={month}
        yearChange={(val) => setYear(val)}
        yearFilter={true}
        yearVal={year}
      />
      <TableLayout
        tblBody={TblBodyJSX}
        tblHead={ControlledTable.TblHead(TableCol(myInfo, handleSortClick))}
        height={tableHeight}
        loading={loading}
        pageRecordCount={rows.length}
        totalRecordCount={pageData.total}
        Pagination={
          <ControlledTable.Pagination
            mainPage={pageData.page}
            limit={pageData.limit}
            total={pageData.total}
            handleClick={handleSetData}
            tableStyle={true}
          />
        }
      />
    </Fragment>
  );
};
export default CompleteCare;
